// ---------------------------------------------------------------------------
// CAPACIDADES DO USUÁRIO — o que ele alcança, perguntado UMA vez ao servidor.
//
// O hub de cada módulo migrado desenha os cards a partir daqui, em vez de uma
// lista de perfis escrita dentro dele. Quem responde é
// `GET /api/acessos/minhas-capacidades`, que roda o MESMO resolver que o
// servidor usa para negar — é isso que impede a tela de discordar da rota.
//
// ⚠️⚠️ ISTO NÃO PROTEGE NADA. Tudo que roda no navegador é editável por quem
//    está usando. Quem nega o dado é o middleware de cada rota; esta lista só
//    decide o que aparece. Uma tela que esconde o botão SEM a trava na rota é
//    cortina, não permissão.
//
// ⚠️ UMA REQUISIÇÃO POR SESSÃO DE PÁGINA, compartilhada: a promessa fica em
//    memória e todos os componentes que perguntam recebem a mesma. Sem isso,
//    cada card do hub abriria a sua — e o hub tem vários.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';

let promessa = null;

// ⚠️⚠️ O ÚLTIMO RESULTADO, guardado para o hook começar JÁ PRONTO. Sem isto, todo
//    componente que pergunta passa por um primeiro render com a lista vazia —
//    e desde 18/09 quem pergunta inclui o MENU LATERAL e o `PrivateRoute`. O
//    sintoma seria o portal piscar sem menu (ou redirecionar para a Home no meio
//    de uma navegação) em cada troca de rota, mesmo com a resposta já em mãos.
let ultimo = null;

/**
 * Busca (uma vez) as capacidades do usuário logado.
 *
 * ⚠️ FALHA FECHADA: se a chamada falhar, devolve conjunto VAZIO — ou seja, o
 *    módulo migrado não mostra card nenhum, e a pessoa vê uma tela pedindo para
 *    recarregar. O contrário (assumir que ela alcança tudo) desenharia botões
 *    que voltam 403 e pareceria defeito do portal.
 */
export function carregarCapacidades() {
    if (promessa) return promessa;

    promessa = (async () => {
        const token = localStorage.getItem('authToken');
        const res = await fetch('/api/acessos/minhas-capacidades', {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Não foi possível carregar suas permissões.');
        const data = await res.json();
        ultimo = {
            capacidades: new Set(data.capacidades || []),
            // Os módulos já migrados — é a fronteira: um módulo fora desta lista
            // continua obedecendo à lista de perfis escrita dentro dele.
            modulos: new Set(data.modulos || []),
        };
        return ultimo;
    })().catch((erro) => {
        // ⚠️ Zera a promessa para a próxima tentativa não herdar a falha: sem
        //    isto, um soluço de rede deixaria o módulo vazio até o F5.
        promessa = null;
        throw erro;
    });

    return promessa;
}

/**
 * ⚠️ Chamar ao SAIR (ou ao trocar de usuário): a lista é de quem estava logado,
 *    e reaproveitá-la para o próximo mostraria os cards da pessoa errada.
 */
export function esquecerCapacidades() {
    promessa = null;
    ultimo = null;
}

/**
 * O hook que os hubs usam.
 *
 * @returns {{caps: Set<string>, carregando: boolean, erro: string,
 *            alcanca: (chave: string) => boolean}}
 */
export function useCapacidades() {
    // Começa do snapshot quando já houver um — ver `ultimo`, acima.
    const [estado, setEstado] = useState(() => (ultimo
        ? { caps: ultimo.capacidades, carregando: false, erro: '' }
        : { caps: new Set(), carregando: true, erro: '' }));

    useEffect(() => {
        let vivo = true;
        carregarCapacidades()
            .then(({ capacidades }) => { if (vivo) setEstado({ caps: capacidades, carregando: false, erro: '' }); })
            .catch((e) => { if (vivo) setEstado({ caps: new Set(), carregando: false, erro: e.message }); });
        return () => { vivo = false; };
    }, []);

    return { ...estado, alcanca: (chave) => estado.caps.has(chave) };
}
